import { createSlice } from '@reduxjs/toolkit';

import {
  completeFollowUp,
  createAdministrativeDistrict,
  createAdministrativeRegion,
  createAssigned,
  createBuilding,
  createCity,
  createCompetitor,
  createCountry,
  createLeadSource,
  createOffer,
  createProduct,
  createStreet,
  createUserPrivilege,
  createWorkflowStatus,
  deleteAdministrativeDistrict,
  deleteAdministrativeRegion,
  deleteAssigned,
  deleteBuilding,
  deleteCity,
  deleteCompetitor,
  deleteCountry,
  deleteLeadSource,
  deleteOffer,
  deletePrivilegeAssignment,
  deleteProduct,
  deleteStreet,
  deleteUserPrivilege,
  deleteWorkflowStatus,
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getAssigned,
  getBuildings,
  getCities,
  getCompetitors,
  getCountries,
  getLeadById,
  getLeadSources,
  getLeadsPool,
  getOffers,
  getPassportData,
  getPrivilegeAssignmentsByPrivilege,
  getProducts,
  getStreets,
  getUserPrivileges,
  getWorkflowStatuses,
  updateAdministrativeDistrict,
  updateAdministrativeRegion,
  updateAssigned,
  updateBuilding,
  updateCity,
  updateCompetitor,
  updateCountry,
  updateLeadSource,
  updateOffer,
  updateProduct,
  updateStreet,
  updateUserPrivilege,
  updateWorkflowStatus,
} from './newsalesActions';

const initialState = {
  // Lead Sources state
  leadSources: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    totalCount: 0,
    searchData: {
      limit: 12,
      offset: 0,
      status: 'enabled',
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Products state
  products: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      status: 'enabled',
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Offers state
  offers: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 4,
      offset: 0,
      status: 'enabled',
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Workflow Statuses state
  workflowStatuses: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Competitors state
  competitors: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 8,
      offset: 0,
      status: 'enabled',
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // User Privileges state
  userPrivileges: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 9,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Privilege Assignments state
  privilegeAssignments: {
    loading: false,
    error: null,
    data: [],
  },

  // Countries state
  countries: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
  },

  // Administrative Regions state
  administrativeRegions: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
    selectedCountryId: null,
  },

  // Cities state
  cities: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
    selectedCountryId: null,
    selectedDistrictId: null,
  },

  // Administrative Districts state
  administrativeDistricts: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
    selectedCountryId: null,
    selectedRegionId: null,
    selectedCityId: null,
  },

  // Streets state
  streets: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
    selectedCountryId: null,
    selectedDistrictId: null,
    selectedCityId: null,
  },

  // Buildings state
  buildings: {
    loading: false,
    error: null,
    success: false,
    data: null,
    currentPage: 1,
    pagesCount: 1,
    searchData: {
      limit: 10,
      offset: 0,
      search: '',
    },
    deleteSuccess: false,
    updateSuccess: false,
    selectedCountryId: null,
    selectedDistrictId: null,
    selectedCityId: null,
    selectedStreetId: null,
    selectedAdministrativeDistrictId: null,
  },

  // Notifications state
  notifications: {
    loading: false,
    error: null,
    success: false,
    data: null,
    deleteSuccess: false,
    updateSuccess: false,
    toggleSuccess: false,
  },
  leadsPool: {
    loading: false,
    error: null,
    data: [],
    totalCount: 0,
    totalConverted: 0,
    totalValue: 0,
  },
  assigned: {
    data: [],
    loading: false,
    error: null,
    success: false,
    currentPage: 1,
    pagesCount: 1,
    totalCount: 0,
    searchData: {},
    createSuccess: false,
    deleteSuccess: false,
    updateSuccess: false,
  },
  followUps: {
    loading: false,
    error: null,
    success: false,
    updateSuccess: false,
    data: [],
  },
  passportData: {
    loading: false,
    error: null,
    data: null,
  },
  leadById: {
    loading: false,
    error: null,
    data: null,
  },
};

const newsalesSlice = createSlice({
  name: 'newsales',
  initialState,
  reducers: {
    setFollowUpsUpdateSuccess: (state, { payload }) => {
      state.followUps.updateSuccess = payload;
    },
    setAssignedCurrentPage: (state, { payload }) => {
      state.assigned.currentPage = payload;
    },

    setAssignedSearchData: (state, { payload }) => {
      state.assigned.searchData = {
        ...state.assigned.searchData,
        ...payload,
      };
      state.assigned.currentPage = 1;
    },

    setAssignedCreateSuccess: (state, { payload }) => {
      state.assigned.createSuccess = payload;
    },

    setAssignedDeleteSuccess: (state, { payload }) => {
      state.assigned.deleteSuccess = payload;
    },

    setAssignedUpdateSuccess: (state, { payload }) => {
      state.assigned.updateSuccess = payload;
    },
    // ==================== LEAD SOURCES REDUCERS ====================
    setLeadSourcesCurrentPage: (state, { payload }) => {
      state.leadSources.currentPage = payload;
    },
    setLeadSourcesSearchData: (state, { payload }) => {
      state.leadSources.searchData = { ...state.leadSources.searchData, ...payload };
    },
    setLeadSourcesCreateSuccess: (state, { payload }) => {
      state.leadSources.success = payload;
    },
    setLeadSourcesDeleteSuccess: (state, { payload }) => {
      state.leadSources.deleteSuccess = payload;
    },
    setLeadSourcesUpdateSuccess: (state, { payload }) => {
      state.leadSources.updateSuccess = payload;
    },

    // ==================== PRODUCTS REDUCERS ====================
    setProductsCurrentPage: (state, { payload }) => {
      state.products.currentPage = payload;
    },
    setProductsSearchData: (state, { payload }) => {
      state.products.searchData = { ...state.products.searchData, ...payload };
    },
    setProductsCreateSuccess: (state, { payload }) => {
      state.products.success = payload;
    },
    setProductsDeleteSuccess: (state, { payload }) => {
      state.products.deleteSuccess = payload;
    },
    setProductsUpdateSuccess: (state, { payload }) => {
      state.products.updateSuccess = payload;
    },

    // ==================== OFFERS REDUCERS ====================
    setOffersCurrentPage: (state, { payload }) => {
      state.offers.currentPage = payload;
    },
    setOffersSearchData: (state, { payload }) => {
      state.offers.searchData = { ...state.offers.searchData, ...payload };
    },
    setOffersCreateSuccess: (state, { payload }) => {
      state.offers.success = payload;
    },
    setOffersDeleteSuccess: (state, { payload }) => {
      state.offers.deleteSuccess = payload;
    },
    setOffersUpdateSuccess: (state, { payload }) => {
      state.offers.updateSuccess = payload;
    },

    // ==================== WORKFLOW STATUSES REDUCERS ====================
    setWorkflowStatusesCurrentPage: (state, { payload }) => {
      state.workflowStatuses.currentPage = payload;
    },
    setWorkflowStatusesSearchData: (state, { payload }) => {
      state.workflowStatuses.searchData = { ...state.workflowStatuses.searchData, ...payload };
    },
    setWorkflowStatusesCreateSuccess: (state, { payload }) => {
      state.workflowStatuses.success = payload;
    },
    setWorkflowStatusesDeleteSuccess: (state, { payload }) => {
      state.workflowStatuses.deleteSuccess = payload;
    },
    setWorkflowStatusesUpdateSuccess: (state, { payload }) => {
      state.workflowStatuses.updateSuccess = payload;
    },

    // ==================== COMPETITORS REDUCERS ====================
    setCompetitorsCurrentPage: (state, { payload }) => {
      state.competitors.currentPage = payload;
    },
    setCompetitorsSearchData: (state, { payload }) => {
      state.competitors.searchData = { ...state.competitors.searchData, ...payload };
    },
    setCompetitorsCreateSuccess: (state, { payload }) => {
      state.competitors.success = payload;
    },
    setCompetitorsDeleteSuccess: (state, { payload }) => {
      state.competitors.deleteSuccess = payload;
    },
    setCompetitorsUpdateSuccess: (state, { payload }) => {
      state.competitors.updateSuccess = payload;
    },

    // ==================== USER PRIVILEGES REDUCERS ====================
    setUserPrivilegesCurrentPage: (state, { payload }) => {
      state.userPrivileges.currentPage = payload;
    },
    setUserPrivilegesSearchData: (state, { payload }) => {
      state.userPrivileges.searchData = { ...state.userPrivileges.searchData, ...payload };
    },
    setUserPrivilegesCreateSuccess: (state, { payload }) => {
      state.userPrivileges.success = payload;
    },
    setUserPrivilegesDeleteSuccess: (state, { payload }) => {
      state.userPrivileges.deleteSuccess = payload;
    },
    setUserPrivilegesUpdateSuccess: (state, { payload }) => {
      state.userPrivileges.updateSuccess = payload;
    },

    // ==================== COUNTRIES REDUCERS ====================
    setCountriesCurrentPage: (state, { payload }) => {
      state.countries.currentPage = payload;
    },
    setCountriesSearchData: (state, { payload }) => {
      state.countries.searchData = { ...state.countries.searchData, ...payload };
    },
    setCountriesCreateSuccess: (state, { payload }) => {
      state.countries.success = payload;
    },
    setCountriesDeleteSuccess: (state, { payload }) => {
      state.countries.deleteSuccess = payload;
    },
    setCountriesUpdateSuccess: (state, { payload }) => {
      state.countries.updateSuccess = payload;
    },

    // ==================== ADMINISTRATIVE REGIONS REDUCERS ====================
    setAdministrativeRegionsCurrentPage: (state, { payload }) => {
      state.administrativeRegions.currentPage = payload;
    },
    setAdministrativeRegionsSearchData: (state, { payload }) => {
      state.administrativeRegions.searchData = {
        ...state.administrativeRegions.searchData,
        ...payload,
      };
    },
    setAdministrativeRegionsCreateSuccess: (state, { payload }) => {
      state.administrativeRegions.success = payload;
    },
    setAdministrativeRegionsDeleteSuccess: (state, { payload }) => {
      state.administrativeRegions.deleteSuccess = payload;
    },
    setAdministrativeRegionsUpdateSuccess: (state, { payload }) => {
      state.administrativeRegions.updateSuccess = payload;
    },
    setAdministrativeRegionsSelectedCountry: (state, { payload }) => {
      state.administrativeRegions.selectedCountryId = payload;
      state.administrativeRegions.currentPage = 1;
      state.administrativeRegions.data = null;
    },

    // ==================== CITIES REDUCERS ====================
    setCitiesCurrentPage: (state, { payload }) => {
      state.cities.currentPage = payload;
    },
    setCitiesSearchData: (state, { payload }) => {
      state.cities.searchData = {
        ...state.cities.searchData,
        ...payload,
      };
    },
    setCitiesCreateSuccess: (state, { payload }) => {
      state.cities.success = payload;
    },
    setCitiesDeleteSuccess: (state, { payload }) => {
      state.cities.deleteSuccess = payload;
    },
    setCitiesUpdateSuccess: (state, { payload }) => {
      state.cities.updateSuccess = payload;
    },
    setCitiesSelectedCountry: (state, { payload }) => {
      state.cities.selectedCountryId = payload;
      state.cities.currentPage = 1;
      state.cities.data = null;
      state.cities.selectedDistrictId = null;
    },
    setCitiesSelectedDistrict: (state, { payload }) => {
      state.cities.selectedDistrictId = payload;
      state.cities.currentPage = 1;
      state.cities.data = null;
    },

    // ==================== ADMINISTRATIVE DISTRICTS REDUCERS ====================
    setAdministrativeDistrictsCurrentPage: (state, { payload }) => {
      state.administrativeDistricts.currentPage = payload;
    },
    setAdministrativeDistrictsSearchData: (state, { payload }) => {
      state.administrativeDistricts.searchData = {
        ...state.administrativeDistricts.searchData,
        ...payload,
      };
    },
    setAdministrativeDistrictsCreateSuccess: (state, { payload }) => {
      state.administrativeDistricts.success = payload;
    },
    setAdministrativeDistrictsDeleteSuccess: (state, { payload }) => {
      state.administrativeDistricts.deleteSuccess = payload;
    },
    setAdministrativeDistrictsUpdateSuccess: (state, { payload }) => {
      state.administrativeDistricts.updateSuccess = payload;
    },
    setAdministrativeDistrictsSelectedCountry: (state, { payload }) => {
      state.administrativeDistricts.selectedCountryId = payload;
      state.administrativeDistricts.currentPage = 1;
      state.administrativeDistricts.data = null;
      state.administrativeDistricts.selectedRegionId = null;
      state.administrativeDistricts.selectedCityId = null;
    },
    setAdministrativeDistrictsSelectedRegion: (state, { payload }) => {
      state.administrativeDistricts.selectedRegionId = payload;
      state.administrativeDistricts.currentPage = 1;
      state.administrativeDistricts.data = null;
      state.administrativeDistricts.selectedCityId = null;
    },
    setAdministrativeDistrictsSelectedCity: (state, { payload }) => {
      state.administrativeDistricts.selectedCityId = payload;
      state.administrativeDistricts.currentPage = 1;
      state.administrativeDistricts.data = null;
    },

    // ==================== STREETS REDUCERS ====================
    setStreetsCurrentPage: (state, { payload }) => {
      state.streets.currentPage = payload;
    },
    setStreetsSearchData: (state, { payload }) => {
      state.streets.searchData = {
        ...state.streets.searchData,
        ...payload,
      };
    },
    setStreetsCreateSuccess: (state, { payload }) => {
      state.streets.success = payload;
    },
    setStreetsDeleteSuccess: (state, { payload }) => {
      state.streets.deleteSuccess = payload;
    },
    setStreetsUpdateSuccess: (state, { payload }) => {
      state.streets.updateSuccess = payload;
    },
    setStreetsSelectedCountry: (state, { payload }) => {
      state.streets.selectedCountryId = payload;
      state.streets.currentPage = 1;
      state.streets.data = null;
      state.streets.selectedDistrictId = null;
      state.streets.selectedCityId = null;
    },
    setStreetsSelectedDistrict: (state, { payload }) => {
      state.streets.selectedDistrictId = payload;
      state.streets.currentPage = 1;
      state.streets.data = null;
      state.streets.selectedCityId = null;
    },
    setStreetsSelectedCity: (state, { payload }) => {
      state.streets.selectedCityId = payload;
      state.streets.currentPage = 1;
      state.streets.data = null;
    },

    // ==================== BUILDINGS REDUCERS ====================
    setBuildingsCurrentPage: (state, { payload }) => {
      state.buildings.currentPage = payload;
    },
    setBuildingsSearchData: (state, { payload }) => {
      state.buildings.searchData = {
        ...state.buildings.searchData,
        ...payload,
      };
    },
    setBuildingsCreateSuccess: (state, { payload }) => {
      state.buildings.success = payload;
    },
    setBuildingsDeleteSuccess: (state, { payload }) => {
      state.buildings.deleteSuccess = payload;
    },
    setBuildingsUpdateSuccess: (state, { payload }) => {
      state.buildings.updateSuccess = payload;
    },
    setBuildingsSelectedCountry: (state, { payload }) => {
      state.buildings.selectedCountryId = payload;
      state.buildings.currentPage = 1;
      state.buildings.data = null;
      state.buildings.selectedDistrictId = null;
      state.buildings.selectedCityId = null;
      state.buildings.selectedStreetId = null;
      state.buildings.selectedAdministrativeDistrictId = null;
    },
    setBuildingsSelectedDistrict: (state, { payload }) => {
      state.buildings.selectedDistrictId = payload;
      state.buildings.currentPage = 1;
      state.buildings.data = null;
      state.buildings.selectedCityId = null;
      state.buildings.selectedStreetId = null;
      state.buildings.selectedAdministrativeDistrictId = null;
    },
    setBuildingsSelectedCity: (state, { payload }) => {
      state.buildings.selectedCityId = payload;
      state.buildings.currentPage = 1;
      state.buildings.data = null;
      state.buildings.selectedStreetId = null;
      state.buildings.selectedAdministrativeDistrictId = null;
    },
    setBuildingsSelectedStreet: (state, { payload }) => {
      state.buildings.selectedStreetId = payload;
      state.buildings.currentPage = 1;
      state.buildings.data = null;
      state.buildings.selectedAdministrativeDistrictId = null;
    },
    setBuildingsSelectedAdministrativeDistrict: (state, { payload }) => {
      state.buildings.selectedAdministrativeDistrictId = payload;
      state.buildings.currentPage = 1;
      state.buildings.data = null;
    },

    // ==================== NOTIFICATIONS REDUCERS ====================
    setNotificationsCreateSuccess: (state, { payload }) => {
      state.notifications.success = payload;
    },
    setNotificationsDeleteSuccess: (state, { payload }) => {
      state.notifications.deleteSuccess = payload;
    },
    setNotificationsUpdateSuccess: (state, { payload }) => {
      state.notifications.updateSuccess = payload;
    },
    setNotificationsToggleSuccess: (state, { payload }) => {
      state.notifications.toggleSuccess = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ==================== GET LEAD SOURCES ====================
      .addCase(getLeadSources.pending, (state) => {
        state.leadSources.loading = true;
        state.leadSources.error = null;
      })
      .addCase(getLeadSources.fulfilled, (state, { payload }) => {
        // Adjust current page if exceeds total pages
        const totalPages = Math.ceil(
          (payload.total || 0) / state.leadSources.searchData.limit
        );
        if (state.leadSources.currentPage > totalPages && totalPages > 0) {
          state.leadSources.currentPage = totalPages;
        }

        state.leadSources.loading = false;
        state.leadSources.error = null;
        state.leadSources.data = payload.data || [];
        state.leadSources.totalCount = payload.total || 0;
        state.leadSources.pagesCount = totalPages || 1;
        state.leadSources.totalCount = payload.total || 0;
      })
      .addCase(getLeadSources.rejected, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = payload;
        state.leadSources.data = [];
      })

      // ==================== CREATE LEAD SOURCE ====================
      .addCase(createLeadSource.pending, (state) => {
        state.leadSources.loading = true;
        state.leadSources.error = null;
        state.leadSources.success = false;
      })
      .addCase(createLeadSource.fulfilled, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = null;
        state.leadSources.success = true;
      })
      .addCase(createLeadSource.rejected, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = payload;
        state.leadSources.success = false;
      })

      // ==================== UPDATE LEAD SOURCE ====================
      .addCase(updateLeadSource.pending, (state) => {
        state.leadSources.loading = true;
        state.leadSources.error = null;
        state.leadSources.updateSuccess = false;
      })
      .addCase(updateLeadSource.fulfilled, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = null;
        state.leadSources.updateSuccess = true;
        // Update item in data array
        if (state.leadSources.data && Array.isArray(state.leadSources.data)) {
          const index = state.leadSources.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.leadSources.data[index] = payload;
          }
        }
      })
      .addCase(updateLeadSource.rejected, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = payload;
        state.leadSources.updateSuccess = false;
      })

      // ==================== DELETE LEAD SOURCE ====================
      .addCase(deleteLeadSource.pending, (state) => {
        state.leadSources.loading = true;
        state.leadSources.error = null;
        state.leadSources.deleteSuccess = false;
      })
      .addCase(deleteLeadSource.fulfilled, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = null;
        state.leadSources.deleteSuccess = true;
        // Remove from data array (optimistic update)
        if (state.leadSources.data && Array.isArray(state.leadSources.data)) {
          state.leadSources.data = state.leadSources.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteLeadSource.rejected, (state, { payload }) => {
        state.leadSources.loading = false;
        state.leadSources.error = payload;
        state.leadSources.deleteSuccess = false;
      })

      // ==================== GET PRODUCTS ====================
      .addCase(getProducts.pending, (state) => {
        state.products.loading = true;
        state.products.error = null;
      })
      .addCase(getProducts.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.products.searchData.limit);
        if (state.products.currentPage > totalPages && totalPages > 0) {
          state.products.currentPage = totalPages;
        }

        state.products.loading = false;
        state.products.error = null;
        state.products.data = payload.data || [];
        state.products.pagesCount = totalPages || 1;
      })
      .addCase(getProducts.rejected, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = payload;
        state.products.data = [];
      })

      // ==================== CREATE PRODUCT ====================
      .addCase(createProduct.pending, (state) => {
        state.products.loading = true;
        state.products.error = null;
        state.products.success = false;
      })
      .addCase(createProduct.fulfilled, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = null;
        state.products.success = true;
      })
      .addCase(createProduct.rejected, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = payload;
        state.products.success = false;
      })

      // ==================== UPDATE PRODUCT ====================
      .addCase(updateProduct.pending, (state) => {
        state.products.loading = true;
        state.products.error = null;
        state.products.updateSuccess = false;
      })
      .addCase(updateProduct.fulfilled, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = null;
        state.products.updateSuccess = true;
        if (state.products.data && Array.isArray(state.products.data)) {
          const index = state.products.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.products.data[index] = payload;
          }
        }
      })
      .addCase(updateProduct.rejected, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = payload;
        state.products.updateSuccess = false;
      })

      // ==================== DELETE PRODUCT ====================
      .addCase(deleteProduct.pending, (state) => {
        state.products.loading = true;
        state.products.error = null;
        state.products.deleteSuccess = false;
      })
      .addCase(deleteProduct.fulfilled, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = null;
        state.products.deleteSuccess = true;
        if (state.products.data && Array.isArray(state.products.data)) {
          state.products.data = state.products.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteProduct.rejected, (state, { payload }) => {
        state.products.loading = false;
        state.products.error = payload;
        state.products.deleteSuccess = false;
      })

      // ==================== GET OFFERS ====================
      .addCase(getOffers.pending, (state) => {
        state.offers.loading = true;
        state.offers.error = null;
      })
      .addCase(getOffers.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.offers.searchData.limit);
        if (state.offers.currentPage > totalPages && totalPages > 0) {
          state.offers.currentPage = totalPages;
        }

        state.offers.loading = false;
        state.offers.error = null;
        state.offers.data = payload.data || [];
        state.offers.pagesCount = totalPages || 1;
      })
      .addCase(getOffers.rejected, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = payload;
        state.offers.data = [];
      })

      // ==================== CREATE OFFER ====================
      .addCase(createOffer.pending, (state) => {
        state.offers.loading = true;
        state.offers.error = null;
        state.offers.success = false;
      })
      .addCase(createOffer.fulfilled, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = null;
        state.offers.success = true;
      })
      .addCase(createOffer.rejected, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = payload;
        state.offers.success = false;
      })

      // ==================== UPDATE OFFER ====================
      .addCase(updateOffer.pending, (state) => {
        state.offers.loading = true;
        state.offers.error = null;
        state.offers.updateSuccess = false;
      })
      .addCase(updateOffer.fulfilled, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = null;
        state.offers.updateSuccess = true;
        if (state.offers.data && Array.isArray(state.offers.data)) {
          const index = state.offers.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.offers.data[index] = payload;
          }
        }
      })
      .addCase(updateOffer.rejected, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = payload;
        state.offers.updateSuccess = false;
      })

      // ==================== DELETE OFFER ====================
      .addCase(deleteOffer.pending, (state) => {
        state.offers.loading = true;
        state.offers.error = null;
        state.offers.deleteSuccess = false;
      })
      .addCase(deleteOffer.fulfilled, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = null;
        state.offers.deleteSuccess = true;
        if (state.offers.data && Array.isArray(state.offers.data)) {
          state.offers.data = state.offers.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteOffer.rejected, (state, { payload }) => {
        state.offers.loading = false;
        state.offers.error = payload;
        state.offers.deleteSuccess = false;
      })

      // ==================== GET WORKFLOW STATUSES ====================
      .addCase(getWorkflowStatuses.pending, (state) => {
        state.workflowStatuses.loading = true;
        state.workflowStatuses.error = null;
      })
      .addCase(getWorkflowStatuses.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil(
          (payload.total || 0) / state.workflowStatuses.searchData.limit
        );
        if (state.workflowStatuses.currentPage > totalPages && totalPages > 0) {
          state.workflowStatuses.currentPage = totalPages;
        }

        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = null;
        state.workflowStatuses.data = payload.data || [];
        state.workflowStatuses.pagesCount = totalPages || 1;
      })
      .addCase(getWorkflowStatuses.rejected, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = payload;
        state.workflowStatuses.data = [];
      })

      // ==================== CREATE WORKFLOW STATUS ====================
      .addCase(createWorkflowStatus.pending, (state) => {
        state.workflowStatuses.loading = true;
        state.workflowStatuses.error = null;
        state.workflowStatuses.success = false;
      })
      .addCase(createWorkflowStatus.fulfilled, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = null;
        state.workflowStatuses.success = true;
      })
      .addCase(createWorkflowStatus.rejected, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = payload;
        state.workflowStatuses.success = false;
      })

      // ==================== UPDATE WORKFLOW STATUS ====================
      .addCase(updateWorkflowStatus.pending, (state) => {
        state.workflowStatuses.loading = true;
        state.workflowStatuses.error = null;
        state.workflowStatuses.updateSuccess = false;
      })
      .addCase(updateWorkflowStatus.fulfilled, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = null;
        state.workflowStatuses.updateSuccess = true;
        if (state.workflowStatuses.data && Array.isArray(state.workflowStatuses.data)) {
          const index = state.workflowStatuses.data.findIndex(
            (item) => item.id === payload.id
          );
          if (index !== -1) {
            state.workflowStatuses.data[index] = payload;
          }
        }
      })
      .addCase(updateWorkflowStatus.rejected, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = payload;
        state.workflowStatuses.updateSuccess = false;
      })

      // ==================== DELETE WORKFLOW STATUS ====================
      .addCase(deleteWorkflowStatus.pending, (state) => {
        state.workflowStatuses.loading = true;
        state.workflowStatuses.error = null;
        state.workflowStatuses.deleteSuccess = false;
      })
      .addCase(deleteWorkflowStatus.fulfilled, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = null;
        state.workflowStatuses.deleteSuccess = true;
        if (state.workflowStatuses.data && Array.isArray(state.workflowStatuses.data)) {
          state.workflowStatuses.data = state.workflowStatuses.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteWorkflowStatus.rejected, (state, { payload }) => {
        state.workflowStatuses.loading = false;
        state.workflowStatuses.error = payload;
        state.workflowStatuses.deleteSuccess = false;
      })

      // ==================== GET COMPETITORS ====================
      .addCase(getCompetitors.pending, (state) => {
        state.competitors.loading = true;
        state.competitors.error = null;
      })
      .addCase(getCompetitors.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil(
          (payload.total || 0) / state.competitors.searchData.limit
        );
        if (state.competitors.currentPage > totalPages && totalPages > 0) {
          state.competitors.currentPage = totalPages;
        }

        state.competitors.loading = false;
        state.competitors.error = null;
        state.competitors.data = payload.data || [];
        state.competitors.pagesCount = totalPages || 1;
      })
      .addCase(getCompetitors.rejected, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = payload;
        state.competitors.data = [];
      })

      // ==================== CREATE COMPETITOR ====================
      .addCase(createCompetitor.pending, (state) => {
        state.competitors.loading = true;
        state.competitors.error = null;
        state.competitors.success = false;
      })
      .addCase(createCompetitor.fulfilled, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = null;
        state.competitors.success = true;
      })
      .addCase(createCompetitor.rejected, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = payload;
        state.competitors.success = false;
      })

      // ==================== UPDATE COMPETITOR ====================
      .addCase(updateCompetitor.pending, (state) => {
        state.competitors.loading = true;
        state.competitors.error = null;
        state.competitors.updateSuccess = false;
      })
      .addCase(updateCompetitor.fulfilled, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = null;
        state.competitors.updateSuccess = true;
        if (state.competitors.data && Array.isArray(state.competitors.data)) {
          const index = state.competitors.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.competitors.data[index] = payload;
          }
        }
      })
      .addCase(updateCompetitor.rejected, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = payload;
        state.competitors.updateSuccess = false;
      })

      // ==================== DELETE COMPETITOR ====================
      .addCase(deleteCompetitor.pending, (state) => {
        state.competitors.loading = true;
        state.competitors.error = null;
        state.competitors.deleteSuccess = false;
      })
      .addCase(deleteCompetitor.fulfilled, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = null;
        state.competitors.deleteSuccess = true;
        if (state.competitors.data && Array.isArray(state.competitors.data)) {
          state.competitors.data = state.competitors.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteCompetitor.rejected, (state, { payload }) => {
        state.competitors.loading = false;
        state.competitors.error = payload;
        state.competitors.deleteSuccess = false;
      })

      // ==================== GET USER PRIVILEGES ====================
      .addCase(getUserPrivileges.pending, (state) => {
        state.userPrivileges.loading = true;
        state.userPrivileges.error = null;
      })
      .addCase(getUserPrivileges.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil(
          (payload.total || 0) / state.userPrivileges.searchData.limit
        );
        if (state.userPrivileges.currentPage > totalPages && totalPages > 0) {
          state.userPrivileges.currentPage = totalPages;
        }

        state.userPrivileges.loading = false;
        state.userPrivileges.data = payload.data || [];
        state.userPrivileges.pagesCount = totalPages;
        state.userPrivileges.error = null;
      })
      .addCase(getUserPrivileges.rejected, (state, { payload }) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = payload;
      })

      // ==================== CREATE USER PRIVILEGE ====================
      .addCase(createUserPrivilege.pending, (state) => {
        state.userPrivileges.loading = true;
        state.userPrivileges.error = null;
        state.userPrivileges.success = false;
      })
      .addCase(createUserPrivilege.fulfilled, (state) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = null;
        state.userPrivileges.success = true;
      })
      .addCase(createUserPrivilege.rejected, (state, { payload }) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = payload;
        state.userPrivileges.success = false;
      })

      // ==================== UPDATE USER PRIVILEGE ====================
      .addCase(updateUserPrivilege.pending, (state) => {
        state.userPrivileges.loading = true;
        state.userPrivileges.error = null;
        state.userPrivileges.updateSuccess = false;
      })
      .addCase(updateUserPrivilege.fulfilled, (state) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = null;
        state.userPrivileges.updateSuccess = true;
      })
      .addCase(updateUserPrivilege.rejected, (state, { payload }) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = payload;
        state.userPrivileges.updateSuccess = false;
      })

      // ==================== DELETE USER PRIVILEGE ====================
      .addCase(deleteUserPrivilege.pending, (state) => {
        state.userPrivileges.loading = true;
        state.userPrivileges.error = null;
        state.userPrivileges.deleteSuccess = false;
      })
      .addCase(deleteUserPrivilege.fulfilled, (state, { payload }) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = null;
        state.userPrivileges.deleteSuccess = true;
        if (state.userPrivileges.data && Array.isArray(state.userPrivileges.data)) {
          state.userPrivileges.data = state.userPrivileges.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteUserPrivilege.rejected, (state, { payload }) => {
        state.userPrivileges.loading = false;
        state.userPrivileges.error = payload;
        state.userPrivileges.deleteSuccess = false;
      })

      // ==================== GET PRIVILEGE ASSIGNMENTS BY PRIVILEGE ====================
      .addCase(getPrivilegeAssignmentsByPrivilege.pending, (state) => {
        state.privilegeAssignments.loading = true;
        state.privilegeAssignments.error = null;
      })
      .addCase(getPrivilegeAssignmentsByPrivilege.fulfilled, (state, { payload }) => {
        state.privilegeAssignments.loading = false;
        state.privilegeAssignments.data = Array.isArray(payload)
          ? payload
          : payload?.assignments || payload?.data || [];
        state.privilegeAssignments.error = null;
      })
      .addCase(getPrivilegeAssignmentsByPrivilege.rejected, (state, { payload }) => {
        state.privilegeAssignments.loading = false;
        state.privilegeAssignments.error = payload;
        state.privilegeAssignments.data = [];
      })

      // ==================== DELETE PRIVILEGE ASSIGNMENT ====================
      .addCase(deletePrivilegeAssignment.pending, (state) => {
        state.privilegeAssignments.error = null;
      })
      .addCase(deletePrivilegeAssignment.fulfilled, (state, { payload }) => {
        state.privilegeAssignments.data = state.privilegeAssignments.data.filter(
          (item) => item.uuid !== payload && item.targetUuid !== payload
        );
      })
      .addCase(deletePrivilegeAssignment.rejected, (state, { payload }) => {
        state.privilegeAssignments.error = payload;
      })

      // ==================== GET COUNTRIES ====================
      .addCase(getCountries.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
      })
      .addCase(getCountries.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.countries.searchData.limit);
        if (state.countries.currentPage > totalPages && totalPages > 0) {
          state.countries.currentPage = totalPages;
        }

        state.countries.loading = false;
        state.countries.error = null;
        state.countries.data = payload.data || [];
        state.countries.pagesCount = totalPages || 1;
      })
      .addCase(getCountries.rejected, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = payload;
        state.countries.data = [];
      })

      // ==================== CREATE COUNTRY ====================
      .addCase(createCountry.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
        state.countries.success = false;
      })
      .addCase(createCountry.fulfilled, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = null;
        state.countries.success = true;
      })
      .addCase(createCountry.rejected, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = payload;
        state.countries.success = false;
      })

      // ==================== UPDATE COUNTRY ====================
      .addCase(updateCountry.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
        state.countries.updateSuccess = false;
      })
      .addCase(updateCountry.fulfilled, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = null;
        state.countries.updateSuccess = true;
        if (state.countries.data && Array.isArray(state.countries.data)) {
          const index = state.countries.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.countries.data[index] = payload;
          }
        }
      })
      .addCase(updateCountry.rejected, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = payload;
        state.countries.updateSuccess = false;
      })

      // ==================== DELETE COUNTRY ====================
      .addCase(deleteCountry.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
        state.countries.deleteSuccess = false;
      })
      .addCase(deleteCountry.fulfilled, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = null;
        state.countries.deleteSuccess = true;
        if (state.countries.data && Array.isArray(state.countries.data)) {
          state.countries.data = state.countries.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteCountry.rejected, (state, { payload }) => {
        state.countries.loading = false;
        state.countries.error = payload;
        state.countries.deleteSuccess = false;
      })

      // ==================== GET ADMINISTRATIVE REGIONS ====================
      .addCase(getAdministrativeRegions.pending, (state) => {
        state.administrativeRegions.loading = true;
        state.administrativeRegions.error = null;
      })
      .addCase(getAdministrativeRegions.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil(
          (payload.total || 0) / state.administrativeRegions.searchData.limit
        );
        if (state.administrativeRegions.currentPage > totalPages && totalPages > 0) {
          state.administrativeRegions.currentPage = totalPages;
        }

        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = null;
        state.administrativeRegions.data = payload.data || [];
        state.administrativeRegions.pagesCount = totalPages || 1;
      })
      .addCase(getAdministrativeRegions.rejected, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = payload;
        state.administrativeRegions.data = [];
      })

      // ==================== CREATE ADMINISTRATIVE REGION ====================
      .addCase(createAdministrativeRegion.pending, (state) => {
        state.administrativeRegions.loading = true;
        state.administrativeRegions.error = null;
        state.administrativeRegions.success = false;
      })
      .addCase(createAdministrativeRegion.fulfilled, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = null;
        state.administrativeRegions.success = true;
      })
      .addCase(createAdministrativeRegion.rejected, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = payload;
        state.administrativeRegions.success = false;
      })

      // ==================== UPDATE ADMINISTRATIVE REGION ====================
      .addCase(updateAdministrativeRegion.pending, (state) => {
        state.administrativeRegions.loading = true;
        state.administrativeRegions.error = null;
        state.administrativeRegions.updateSuccess = false;
      })
      .addCase(updateAdministrativeRegion.fulfilled, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = null;
        state.administrativeRegions.updateSuccess = true;
        if (
          state.administrativeRegions.data &&
          Array.isArray(state.administrativeRegions.data)
        ) {
          const index = state.administrativeRegions.data.findIndex(
            (item) => item.id === payload.id
          );
          if (index !== -1) {
            state.administrativeRegions.data[index] = payload;
          }
        }
      })
      .addCase(updateAdministrativeRegion.rejected, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = payload;
        state.administrativeRegions.updateSuccess = false;
      })

      // ==================== DELETE ADMINISTRATIVE REGION ====================
      .addCase(deleteAdministrativeRegion.pending, (state) => {
        state.administrativeRegions.loading = true;
        state.administrativeRegions.error = null;
        state.administrativeRegions.deleteSuccess = false;
      })
      .addCase(deleteAdministrativeRegion.fulfilled, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = null;
        state.administrativeRegions.deleteSuccess = true;
        if (
          state.administrativeRegions.data &&
          Array.isArray(state.administrativeRegions.data)
        ) {
          state.administrativeRegions.data = state.administrativeRegions.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteAdministrativeRegion.rejected, (state, { payload }) => {
        state.administrativeRegions.loading = false;
        state.administrativeRegions.error = payload;
        state.administrativeRegions.deleteSuccess = false;
      })

      // ==================== GET CITIES ====================
      .addCase(getCities.pending, (state) => {
        state.cities.loading = true;
        state.cities.error = null;
      })
      .addCase(getCities.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.cities.searchData.limit);
        if (state.cities.currentPage > totalPages && totalPages > 0) {
          state.cities.currentPage = totalPages;
        }

        state.cities.loading = false;
        state.cities.error = null;
        state.cities.data = payload.data || [];
        state.cities.pagesCount = totalPages || 1;
      })
      .addCase(getCities.rejected, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = payload;
        state.cities.data = [];
      })

      // ==================== CREATE CITY ====================
      .addCase(createCity.pending, (state) => {
        state.cities.loading = true;
        state.cities.error = null;
        state.cities.success = false;
      })
      .addCase(createCity.fulfilled, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = null;
        state.cities.success = true;
      })
      .addCase(createCity.rejected, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = payload;
        state.cities.success = false;
      })

      // ==================== UPDATE CITY ====================
      .addCase(updateCity.pending, (state) => {
        state.cities.loading = true;
        state.cities.error = null;
        state.cities.updateSuccess = false;
      })
      .addCase(updateCity.fulfilled, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = null;
        state.cities.updateSuccess = true;
        if (state.cities.data && Array.isArray(state.cities.data)) {
          const index = state.cities.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.cities.data[index] = payload;
          }
        }
      })
      .addCase(updateCity.rejected, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = payload;
        state.cities.updateSuccess = false;
      })

      // ==================== DELETE CITY ====================
      .addCase(deleteCity.pending, (state) => {
        state.cities.loading = true;
        state.cities.error = null;
        state.cities.deleteSuccess = false;
      })
      .addCase(deleteCity.fulfilled, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = null;
        state.cities.deleteSuccess = true;
        if (state.cities.data && Array.isArray(state.cities.data)) {
          state.cities.data = state.cities.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteCity.rejected, (state, { payload }) => {
        state.cities.loading = false;
        state.cities.error = payload;
        state.cities.deleteSuccess = false;
      })

      // ==================== GET ADMINISTRATIVE DISTRICTS ====================
      .addCase(getAdministrativeDistricts.pending, (state) => {
        state.administrativeDistricts.loading = true;
        state.administrativeDistricts.error = null;
      })
      .addCase(getAdministrativeDistricts.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil(
          (payload.total || 0) / state.administrativeDistricts.searchData.limit
        );
        if (state.administrativeDistricts.currentPage > totalPages && totalPages > 0) {
          state.administrativeDistricts.currentPage = totalPages;
        }

        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.data = payload.data || [];
        state.administrativeDistricts.pagesCount = totalPages || 1;
      })
      .addCase(getAdministrativeDistricts.rejected, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = payload;
        state.administrativeDistricts.data = [];
      })

      // ==================== CREATE ADMINISTRATIVE DISTRICT ====================
      .addCase(createAdministrativeDistrict.pending, (state) => {
        state.administrativeDistricts.loading = true;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.success = false;
      })
      .addCase(createAdministrativeDistrict.fulfilled, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.success = true;
      })
      .addCase(createAdministrativeDistrict.rejected, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = payload;
        state.administrativeDistricts.success = false;
      })

      // ==================== UPDATE ADMINISTRATIVE DISTRICT ====================
      .addCase(updateAdministrativeDistrict.pending, (state) => {
        state.administrativeDistricts.loading = true;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.updateSuccess = false;
      })
      .addCase(updateAdministrativeDistrict.fulfilled, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.updateSuccess = true;
        if (
          state.administrativeDistricts.data &&
          Array.isArray(state.administrativeDistricts.data)
        ) {
          const index = state.administrativeDistricts.data.findIndex(
            (item) => item.id === payload.id
          );
          if (index !== -1) {
            state.administrativeDistricts.data[index] = payload;
          }
        }
      })
      .addCase(updateAdministrativeDistrict.rejected, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = payload;
        state.administrativeDistricts.updateSuccess = false;
      })

      // ==================== DELETE ADMINISTRATIVE DISTRICT ====================
      .addCase(deleteAdministrativeDistrict.pending, (state) => {
        state.administrativeDistricts.loading = true;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.deleteSuccess = false;
      })
      .addCase(deleteAdministrativeDistrict.fulfilled, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = null;
        state.administrativeDistricts.deleteSuccess = true;
        if (
          state.administrativeDistricts.data &&
          Array.isArray(state.administrativeDistricts.data)
        ) {
          state.administrativeDistricts.data = state.administrativeDistricts.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase(deleteAdministrativeDistrict.rejected, (state, { payload }) => {
        state.administrativeDistricts.loading = false;
        state.administrativeDistricts.error = payload;
        state.administrativeDistricts.deleteSuccess = false;
      })

      // ==================== GET STREETS ====================
      .addCase(getStreets.pending, (state) => {
        state.streets.loading = true;
        state.streets.error = null;
      })
      .addCase(getStreets.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.streets.searchData.limit);
        if (state.streets.currentPage > totalPages && totalPages > 0) {
          state.streets.currentPage = totalPages;
        }

        state.streets.loading = false;
        state.streets.error = null;
        state.streets.data = payload.data || [];
        state.streets.pagesCount = totalPages || 1;
      })
      .addCase(getStreets.rejected, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = payload;
        state.streets.data = [];
      })

      // ==================== CREATE STREET ====================
      .addCase(createStreet.pending, (state) => {
        state.streets.loading = true;
        state.streets.error = null;
        state.streets.success = false;
      })
      .addCase(createStreet.fulfilled, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = null;
        state.streets.success = true;
      })
      .addCase(createStreet.rejected, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = payload;
        state.streets.success = false;
      })

      // ==================== UPDATE STREET ====================
      .addCase(updateStreet.pending, (state) => {
        state.streets.loading = true;
        state.streets.error = null;
        state.streets.updateSuccess = false;
      })
      .addCase(updateStreet.fulfilled, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = null;
        state.streets.updateSuccess = true;
        if (state.streets.data && Array.isArray(state.streets.data)) {
          const index = state.streets.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.streets.data[index] = payload;
          }
        }
      })
      .addCase(updateStreet.rejected, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = payload;
        state.streets.updateSuccess = false;
      })

      // ==================== DELETE STREET ====================
      .addCase(deleteStreet.pending, (state) => {
        state.streets.loading = true;
        state.streets.error = null;
        state.streets.deleteSuccess = false;
      })
      .addCase(deleteStreet.fulfilled, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = null;
        state.streets.deleteSuccess = true;
        if (state.streets.data && Array.isArray(state.streets.data)) {
          state.streets.data = state.streets.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteStreet.rejected, (state, { payload }) => {
        state.streets.loading = false;
        state.streets.error = payload;
        state.streets.deleteSuccess = false;
      })

      // ==================== GET BUILDINGS ====================
      .addCase(getBuildings.pending, (state) => {
        state.buildings.loading = true;
        state.buildings.error = null;
      })
      .addCase(getBuildings.fulfilled, (state, { payload }) => {
        const totalPages = Math.ceil((payload.total || 0) / state.buildings.searchData.limit);
        if (state.buildings.currentPage > totalPages && totalPages > 0) {
          state.buildings.currentPage = totalPages;
        }

        state.buildings.loading = false;
        state.buildings.error = null;
        state.buildings.data = payload.data || [];
        state.buildings.pagesCount = totalPages || 1;
      })
      .addCase(getBuildings.rejected, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = payload;
        state.buildings.data = [];
      })

      // ==================== CREATE BUILDING ====================
      .addCase(createBuilding.pending, (state) => {
        state.buildings.loading = true;
        state.buildings.error = null;
        state.buildings.success = false;
      })
      .addCase(createBuilding.fulfilled, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = null;
        state.buildings.success = true;
      })
      .addCase(createBuilding.rejected, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = payload;
        state.buildings.success = false;
      })

      // ==================== UPDATE BUILDING ====================
      .addCase(updateBuilding.pending, (state) => {
        state.buildings.loading = true;
        state.buildings.error = null;
        state.buildings.updateSuccess = false;
      })
      .addCase(updateBuilding.fulfilled, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = null;
        state.buildings.updateSuccess = true;
        if (state.buildings.data && Array.isArray(state.buildings.data)) {
          const index = state.buildings.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.buildings.data[index] = payload;
          }
        }
      })
      .addCase(updateBuilding.rejected, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = payload;
        state.buildings.updateSuccess = false;
      })

      // ==================== DELETE BUILDING ====================
      .addCase(deleteBuilding.pending, (state) => {
        state.buildings.loading = true;
        state.buildings.error = null;
        state.buildings.deleteSuccess = false;
      })
      .addCase(deleteBuilding.fulfilled, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = null;
        state.buildings.deleteSuccess = true;
        if (state.buildings.data && Array.isArray(state.buildings.data)) {
          state.buildings.data = state.buildings.data.filter((item) => item.id !== payload.id);
        }
      })
      .addCase(deleteBuilding.rejected, (state, { payload }) => {
        state.buildings.loading = false;
        state.buildings.error = payload;
        state.buildings.deleteSuccess = false;
      })

      // ==================== GET NOTIFICATIONS ====================
      .addCase('newsales/notifications/get/pending', (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
      })
      .addCase('newsales/notifications/get/fulfilled', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = null;
        state.notifications.data = payload.data || [];
      })
      .addCase('newsales/notifications/get/rejected', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = payload;
        state.notifications.data = [];
      })

      // ==================== CREATE NOTIFICATION ====================
      .addCase('newsales/notifications/create/pending', (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
        state.notifications.success = false;
      })
      .addCase('newsales/notifications/create/fulfilled', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = null;
        state.notifications.success = true;
      })
      .addCase('newsales/notifications/create/rejected', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = payload;
        state.notifications.success = false;
      })

      // ==================== UPDATE NOTIFICATION ====================
      .addCase('newsales/notifications/update/pending', (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
        state.notifications.updateSuccess = false;
      })
      .addCase('newsales/notifications/update/fulfilled', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = null;
        state.notifications.updateSuccess = true;
      })
      .addCase('newsales/notifications/update/rejected', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = payload;
        state.notifications.updateSuccess = false;
      })

      // ==================== DELETE NOTIFICATION ====================
      .addCase('newsales/notifications/delete/pending', (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
        state.notifications.deleteSuccess = false;
      })
      .addCase('newsales/notifications/delete/fulfilled', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = null;
        state.notifications.deleteSuccess = true;
        if (state.notifications.data && Array.isArray(state.notifications.data)) {
          state.notifications.data = state.notifications.data.filter(
            (item) => item.id !== payload.id
          );
        }
      })
      .addCase('newsales/notifications/delete/rejected', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = payload;
        state.notifications.deleteSuccess = false;
      })

      // ==================== TOGGLE NOTIFICATION ====================
      .addCase('newsales/notifications/toggle/pending', (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
        state.notifications.toggleSuccess = false;
      })
      .addCase('newsales/notifications/toggle/fulfilled', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = null;
        state.notifications.toggleSuccess = true;
        if (state.notifications.data && Array.isArray(state.notifications.data)) {
          const index = state.notifications.data.findIndex((item) => item.id === payload.id);
          if (index !== -1) {
            state.notifications.data[index] = payload;
          }
        }
      })
      .addCase('newsales/notifications/toggle/rejected', (state, { payload }) => {
        state.notifications.loading = false;
        state.notifications.error = payload;
        state.notifications.toggleSuccess = false;
      })

      .addCase(getLeadsPool.pending, (state) => {
        state.leadsPool.loading = true;
        state.leadsPool.error = null;
      })
      .addCase(getLeadsPool.fulfilled, (state, { payload }) => {
        state.leadsPool.loading = false;
        state.leadsPool.error = null;
        state.leadsPool.data = payload.data || [];
        state.leadsPool.totalCount = payload.total || 0;
        state.leadsPool.totalConverted = payload.totalConverted || 0;
        state.leadsPool.totalOrdersSum = payload.totalValue || 0;
      })
      .addCase(getLeadsPool.rejected, (state, { payload }) => {
        state.leadsPool.loading = false;
        state.leadsPool.error = payload;
        state.leadsPool.data = [];
      })
      .addCase(createAssigned.pending, (state) => {
        state.assigned.loading = true;
        state.assigned.error = null;
        state.assigned.createSuccess = false;
      })
      .addCase(createAssigned.fulfilled, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.createSuccess = true;

        state.assigned.data = [payload, ...state.assigned.data];
      })
      .addCase(createAssigned.rejected, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.error = payload;
        state.assigned.createSuccess = false;
      })
      .addCase(updateAssigned.pending, (state) => {
        state.assigned.loading = true;
        state.assigned.error = null;
        state.assigned.updateSuccess = false;
      })
      .addCase(updateAssigned.fulfilled, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.updateSuccess = true;

        const index = state.assigned.data.findIndex((item) => item.id === payload.id);

        if (index !== -1) {
          state.assigned.data[index] = payload;
        }
      })
      .addCase(updateAssigned.rejected, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.error = payload;
        state.assigned.updateSuccess = false;
      })
      .addCase(deleteAssigned.pending, (state) => {
        state.assigned.loading = true;
        state.assigned.error = null;
        state.assigned.deleteSuccess = false;
      })
      .addCase(deleteAssigned.fulfilled, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.deleteSuccess = true;

        state.assigned.data = state.assigned.data.filter((item) => item.id !== payload.id);
      })
      .addCase(deleteAssigned.rejected, (state, { payload }) => {
        state.assigned.loading = false;
        state.assigned.error = payload;
        state.assigned.deleteSuccess = false;
      })

      .addCase(completeFollowUp.pending, (state) => {
        state.followUps.loading = true;
        state.followUps.error = null;
        state.followUps.updateSuccess = false;
      })

      .addCase(completeFollowUp.fulfilled, (state, { payload }) => {
        state.followUps.loading = false;
        state.followUps.error = null;
        state.followUps.updateSuccess = true;

        if (Array.isArray(state.followUps.data)) {
          const index = state.followUps.data.findIndex((item) => item.id === payload.id);

          if (index !== -1) {
            state.followUps.data[index] = payload;
          }
        }
      })

      .addCase(completeFollowUp.rejected, (state, { payload }) => {
        state.followUps.loading = false;
        state.followUps.error = payload;
        state.followUps.updateSuccess = false;
      });
    builder.addCase(getAssigned.fulfilled, (state, action) => {
      state.assigned = action.payload || [];
    });

    // Passport Data
    builder
      .addCase(getPassportData.pending, (state) => {
        state.passportData.loading = true;
        state.passportData.error = null;
      })
      .addCase(getPassportData.fulfilled, (state, { payload }) => {
        state.passportData.loading = false;
        state.passportData.data = payload;
      })
      .addCase(getPassportData.rejected, (state, { payload }) => {
        state.passportData.loading = false;
        state.passportData.error = payload;
      });

    builder
      .addCase(getLeadById.pending, (state) => {
        state.leadById.loading = true;
        state.leadById.error = null;
      })
      .addCase(getLeadById.fulfilled, (state, { payload }) => {
        state.leadById.loading = false;
        state.leadById.data = payload;
      })
      .addCase(getLeadById.rejected, (state, { payload }) => {
        state.leadById.loading = false;
        state.leadById.error = payload;
      });
  },
});

// Export action creators
export const {
  setLeadSourcesCurrentPage,
  setLeadSourcesSearchData,
  setLeadSourcesCreateSuccess,
  setLeadSourcesDeleteSuccess,
  setLeadSourcesUpdateSuccess,
  setProductsCurrentPage,
  setProductsSearchData,
  setProductsCreateSuccess,
  setProductsDeleteSuccess,
  setProductsUpdateSuccess,
  setOffersCurrentPage,
  setOffersSearchData,
  setOffersCreateSuccess,
  setOffersDeleteSuccess,
  setOffersUpdateSuccess,
  setWorkflowStatusesCurrentPage,
  setWorkflowStatusesSearchData,
  setWorkflowStatusesCreateSuccess,
  setWorkflowStatusesDeleteSuccess,
  setWorkflowStatusesUpdateSuccess,
  setCompetitorsCurrentPage,
  setCompetitorsSearchData,
  setCompetitorsCreateSuccess,
  setCompetitorsDeleteSuccess,
  setCompetitorsUpdateSuccess,
  setUserPrivilegesCurrentPage,
  setUserPrivilegesSearchData,
  setUserPrivilegesCreateSuccess,
  setUserPrivilegesDeleteSuccess,
  setUserPrivilegesUpdateSuccess,
  setCountriesCurrentPage,
  setCountriesSearchData,
  setCountriesCreateSuccess,
  setCountriesDeleteSuccess,
  setCountriesUpdateSuccess,
  setAdministrativeRegionsCurrentPage,
  setAdministrativeRegionsSearchData,
  setAdministrativeRegionsCreateSuccess,
  setAdministrativeRegionsDeleteSuccess,
  setAdministrativeRegionsUpdateSuccess,
  setAdministrativeRegionsSelectedCountry,
  setCitiesCurrentPage,
  setCitiesSearchData,
  setCitiesCreateSuccess,
  setCitiesDeleteSuccess,
  setCitiesUpdateSuccess,
  setCitiesSelectedCountry,
  setCitiesSelectedDistrict,
  setAdministrativeDistrictsCurrentPage,
  setAdministrativeDistrictsSearchData,
  setAdministrativeDistrictsCreateSuccess,
  setAdministrativeDistrictsDeleteSuccess,
  setAdministrativeDistrictsUpdateSuccess,
  setAdministrativeDistrictsSelectedCountry,
  setAdministrativeDistrictsSelectedRegion,
  setAdministrativeDistrictsSelectedCity,
  setStreetsCurrentPage,
  setStreetsSearchData,
  setStreetsCreateSuccess,
  setStreetsDeleteSuccess,
  setStreetsUpdateSuccess,
  setStreetsSelectedCountry,
  setStreetsSelectedDistrict,
  setStreetsSelectedCity,
  setBuildingsCurrentPage,
  setBuildingsSearchData,
  setBuildingsCreateSuccess,
  setBuildingsDeleteSuccess,
  setBuildingsUpdateSuccess,
  setBuildingsSelectedCountry,
  setBuildingsSelectedDistrict,
  setBuildingsSelectedCity,
  setBuildingsSelectedStreet,
  setBuildingsSelectedAdministrativeDistrict,
  setNotificationsCreateSuccess,
  setNotificationsDeleteSuccess,
  setNotificationsUpdateSuccess,
  setNotificationsToggleSuccess,
  setAssignedCurrentPage,
  setAssignedSearchData,
  setAssignedCreateSuccess,
  setAssignedDeleteSuccess,
  setAssignedUpdateSuccess,
  setFollowUpsUpdateSuccess,
} = newsalesSlice.actions;

// ==================== SELECTORS ====================

// Lead Sources selectors
export const selectLeadSources = (state) => state.newsales.leadSources.data;
export const selectLeadSourcesLoading = (state) => state.newsales.leadSources.loading;
export const selectLeadSourcesError = (state) => state.newsales.leadSources.error;
export const selectLeadSourcesSuccess = (state) => state.newsales.leadSources.success;
export const selectLeadSourcesCurrentPage = (state) => state.newsales.leadSources.currentPage;
export const selectLeadSourcesPagesCount = (state) => state.newsales.leadSources.pagesCount;
export const selectLeadSourcesTotalCount = (state) => state.newsales.leadSources.totalCount;
export const selectLeadSourcesSearchData = (state) => state.newsales.leadSources.searchData;
export const selectLeadSourcesDeleteSuccess = (state) =>
  state.newsales.leadSources.deleteSuccess;
export const selectLeadSourcesUpdateSuccess = (state) =>
  state.newsales.leadSources.updateSuccess;

// Products selectors
export const selectProducts = (state) => state.newsales.products.data;
export const selectProductsLoading = (state) => state.newsales.products.loading;
export const selectProductsError = (state) => state.newsales.products.error;
export const selectProductsSuccess = (state) => state.newsales.products.success;
export const selectProductsCurrentPage = (state) => state.newsales.products.currentPage;
export const selectProductsPagesCount = (state) => state.newsales.products.pagesCount;
export const selectProductsSearchData = (state) => state.newsales.products.searchData;
export const selectProductsDeleteSuccess = (state) => state.newsales.products.deleteSuccess;
export const selectProductsUpdateSuccess = (state) => state.newsales.products.updateSuccess;

// Offers selectors
export const selectOffers = (state) => state.newsales.offers.data;
export const selectOffersLoading = (state) => state.newsales.offers.loading;
export const selectOffersError = (state) => state.newsales.offers.error;
export const selectOffersSuccess = (state) => state.newsales.offers.success;
export const selectOffersCurrentPage = (state) => state.newsales.offers.currentPage;
export const selectOffersPagesCount = (state) => state.newsales.offers.pagesCount;
export const selectOffersSearchData = (state) => state.newsales.offers.searchData;
export const selectOffersDeleteSuccess = (state) => state.newsales.offers.deleteSuccess;
export const selectOffersUpdateSuccess = (state) => state.newsales.offers.updateSuccess;

// Workflow Statuses selectors
export const selectWorkflowStatuses = (state) => state.newsales.workflowStatuses.data;
export const selectWorkflowStatusesLoading = (state) =>
  state.newsales.workflowStatuses.loading;
export const selectWorkflowStatusesError = (state) => state.newsales.workflowStatuses.error;
export const selectWorkflowStatusesSuccess = (state) =>
  state.newsales.workflowStatuses.success;
export const selectWorkflowStatusesCurrentPage = (state) =>
  state.newsales.workflowStatuses.currentPage;
export const selectWorkflowStatusesPagesCount = (state) =>
  state.newsales.workflowStatuses.pagesCount;
export const selectWorkflowStatusesSearchData = (state) =>
  state.newsales.workflowStatuses.searchData;
export const selectWorkflowStatusesDeleteSuccess = (state) =>
  state.newsales.workflowStatuses.deleteSuccess;
export const selectWorkflowStatusesUpdateSuccess = (state) =>
  state.newsales.workflowStatuses.updateSuccess;

// Competitors selectors
export const selectCompetitors = (state) => state.newsales.competitors.data;
export const selectCompetitorsLoading = (state) => state.newsales.competitors.loading;
export const selectCompetitorsError = (state) => state.newsales.competitors.error;
export const selectCompetitorsSuccess = (state) => state.newsales.competitors.success;
export const selectCompetitorsCurrentPage = (state) => state.newsales.competitors.currentPage;
export const selectCompetitorsPagesCount = (state) => state.newsales.competitors.pagesCount;
export const selectCompetitorsSearchData = (state) => state.newsales.competitors.searchData;
export const selectCompetitorsDeleteSuccess = (state) =>
  state.newsales.competitors.deleteSuccess;
export const selectCompetitorsUpdateSuccess = (state) =>
  state.newsales.competitors.updateSuccess;

// User Privileges selectors
export const selectUserPrivileges = (state) => state.newsales.userPrivileges.data;
export const selectUserPrivilegesLoading = (state) => state.newsales.userPrivileges.loading;
export const selectUserPrivilegesError = (state) => state.newsales.userPrivileges.error;
export const selectUserPrivilegesSuccess = (state) => state.newsales.userPrivileges.success;
export const selectUserPrivilegesCurrentPage = (state) =>
  state.newsales.userPrivileges.currentPage;
export const selectUserPrivilegesPagesCount = (state) =>
  state.newsales.userPrivileges.pagesCount;
export const selectUserPrivilegesSearchData = (state) =>
  state.newsales.userPrivileges.searchData;
export const selectUserPrivilegesDeleteSuccess = (state) =>
  state.newsales.userPrivileges.deleteSuccess;
export const selectUserPrivilegesUpdateSuccess = (state) =>
  state.newsales.userPrivileges.updateSuccess;

// Countries selectors
export const selectCountries = (state) => state.newsales.countries.data;
export const selectCountriesLoading = (state) => state.newsales.countries.loading;
export const selectCountriesError = (state) => state.newsales.countries.error;
export const selectCountriesSuccess = (state) => state.newsales.countries.success;
export const selectCountriesCurrentPage = (state) => state.newsales.countries.currentPage;
export const selectCountriesPagesCount = (state) => state.newsales.countries.pagesCount;
export const selectCountriesSearchData = (state) => state.newsales.countries.searchData;
export const selectCountriesDeleteSuccess = (state) => state.newsales.countries.deleteSuccess;
export const selectCountriesUpdateSuccess = (state) => state.newsales.countries.updateSuccess;

// Administrative Regions selectors
export const selectAdministrativeRegions = (state) =>
  state.newsales.administrativeRegions.data;
export const selectAdministrativeRegionsLoading = (state) =>
  state.newsales.administrativeRegions.loading;
export const selectAdministrativeRegionsError = (state) =>
  state.newsales.administrativeRegions.error;
export const selectAdministrativeRegionsSuccess = (state) =>
  state.newsales.administrativeRegions.success;
export const selectAdministrativeRegionsCurrentPage = (state) =>
  state.newsales.administrativeRegions.currentPage;
export const selectAdministrativeRegionsPagesCount = (state) =>
  state.newsales.administrativeRegions.pagesCount;
export const selectAdministrativeRegionsSearchData = (state) =>
  state.newsales.administrativeRegions.searchData;
export const selectAdministrativeRegionsDeleteSuccess = (state) =>
  state.newsales.administrativeRegions.deleteSuccess;
export const selectAdministrativeRegionsUpdateSuccess = (state) =>
  state.newsales.administrativeRegions.updateSuccess;
export const selectAdministrativeRegionsSelectedCountryId = (state) =>
  state.newsales.administrativeRegions.selectedCountryId;

// Cities selectors
export const selectCities = (state) => state.newsales.cities.data;
export const selectCitiesLoading = (state) => state.newsales.cities.loading;
export const selectCitiesError = (state) => state.newsales.cities.error;
export const selectCitiesSuccess = (state) => state.newsales.cities.success;
export const selectCitiesCurrentPage = (state) => state.newsales.cities.currentPage;
export const selectCitiesPagesCount = (state) => state.newsales.cities.pagesCount;
export const selectCitiesSearchData = (state) => state.newsales.cities.searchData;
export const selectCitiesDeleteSuccess = (state) => state.newsales.cities.deleteSuccess;
export const selectCitiesUpdateSuccess = (state) => state.newsales.cities.updateSuccess;
export const selectCitiesSelectedCountryId = (state) =>
  state.newsales.cities.selectedCountryId;
export const selectCitiesSelectedDistrictId = (state) =>
  state.newsales.cities.selectedDistrictId;

// Administrative Districts selectors
export const selectAdministrativeDistricts = (state) =>
  state.newsales.administrativeDistricts.data;
export const selectAdministrativeDistrictsLoading = (state) =>
  state.newsales.administrativeDistricts.loading;
export const selectAdministrativeDistrictsError = (state) =>
  state.newsales.administrativeDistricts.error;
export const selectAdministrativeDistrictsSuccess = (state) =>
  state.newsales.administrativeDistricts.success;
export const selectAdministrativeDistrictsCurrentPage = (state) =>
  state.newsales.administrativeDistricts.currentPage;
export const selectAdministrativeDistrictsPagesCount = (state) =>
  state.newsales.administrativeDistricts.pagesCount;
export const selectAdministrativeDistrictsSearchData = (state) =>
  state.newsales.administrativeDistricts.searchData;
export const selectAdministrativeDistrictsDeleteSuccess = (state) =>
  state.newsales.administrativeDistricts.deleteSuccess;
export const selectAdministrativeDistrictsUpdateSuccess = (state) =>
  state.newsales.administrativeDistricts.updateSuccess;
export const selectAdministrativeDistrictsSelectedCountryId = (state) =>
  state.newsales.administrativeDistricts.selectedCountryId;
export const selectAdministrativeDistrictsSelectedRegionId = (state) =>
  state.newsales.administrativeDistricts.selectedRegionId;
export const selectAdministrativeDistrictsSelectedCityId = (state) =>
  state.newsales.administrativeDistricts.selectedCityId;

// Streets selectors
export const selectStreets = (state) => state.newsales.streets.data;
export const selectStreetsLoading = (state) => state.newsales.streets.loading;
export const selectStreetsError = (state) => state.newsales.streets.error;
export const selectStreetsSuccess = (state) => state.newsales.streets.success;
export const selectStreetsCurrentPage = (state) => state.newsales.streets.currentPage;
export const selectStreetsPagesCount = (state) => state.newsales.streets.pagesCount;
export const selectStreetsSearchData = (state) => state.newsales.streets.searchData;
export const selectStreetsDeleteSuccess = (state) => state.newsales.streets.deleteSuccess;
export const selectStreetsUpdateSuccess = (state) => state.newsales.streets.updateSuccess;
export const selectStreetsSelectedCountryId = (state) =>
  state.newsales.streets.selectedCountryId;
export const selectStreetsSelectedDistrictId = (state) =>
  state.newsales.streets.selectedDistrictId;
export const selectStreetsSelectedCityId = (state) => state.newsales.streets.selectedCityId;

// Buildings selectors
export const selectBuildings = (state) => state.newsales.buildings.data;
export const selectBuildingsLoading = (state) => state.newsales.buildings.loading;
export const selectBuildingsError = (state) => state.newsales.buildings.error;
export const selectBuildingsSuccess = (state) => state.newsales.buildings.success;
export const selectBuildingsCurrentPage = (state) => state.newsales.buildings.currentPage;
export const selectBuildingsPagesCount = (state) => state.newsales.buildings.pagesCount;
export const selectBuildingsSearchData = (state) => state.newsales.buildings.searchData;
export const selectBuildingsDeleteSuccess = (state) => state.newsales.buildings.deleteSuccess;
export const selectBuildingsUpdateSuccess = (state) => state.newsales.buildings.updateSuccess;
export const selectBuildingsSelectedCountryId = (state) =>
  state.newsales.buildings.selectedCountryId;
export const selectBuildingsSelectedDistrictId = (state) =>
  state.newsales.buildings.selectedDistrictId;
export const selectBuildingsSelectedCityId = (state) =>
  state.newsales.buildings.selectedCityId;
export const selectBuildingsSelectedStreetId = (state) =>
  state.newsales.buildings.selectedStreetId;
export const selectBuildingsSelectedAdministrativeDistrictId = (state) =>
  state.newsales.buildings.selectedAdministrativeDistrictId;

// Notifications selectors
export const selectNotifications = (state) => state.newsales.notifications.data;
export const selectNotificationsLoading = (state) => state.newsales.notifications.loading;
export const selectNotificationsError = (state) => state.newsales.notifications.error;
export const selectNotificationsSuccess = (state) => state.newsales.notifications.success;
export const selectNotificationsDeleteSuccess = (state) =>
  state.newsales.notifications.deleteSuccess;
export const selectNotificationsUpdateSuccess = (state) =>
  state.newsales.notifications.updateSuccess;
export const selectNotificationsToggleSuccess = (state) =>
  state.newsales.notifications.toggleSuccess;

// Leads Pool selectors
export const selectLeadsPool = (state) => state.newsales.leadsPool.data;
export const selectLeadsPoolLoading = (state) => state.newsales.leadsPool.loading;
export const selectLeadsPoolError = (state) => state.newsales.leadsPool.error;
export const selectLeadsPoolPagesCount = (state) => state.newsales.leadsPool.totalCount;
export const selectLeadsPoolTotalConverted = (state) =>
  state.newsales.leadsPool.totalConverted;
export const selectLeadsPoolTotalOrdersSum = (state) =>
  state.newsales.leadsPool.totalOrdersSum;

// Privilege Assignments selectors
export const selectPrivilegeAssignments = (state) => state.newsales.privilegeAssignments.data;
export const selectPrivilegeAssignmentsLoading = (state) =>
  state.newsales.privilegeAssignments.loading;

// Export the reducer

// Assigned selectors
export const selectAssigned = (state) => state.newsales.assigned || [];
export const selectAssignedLoading = (state) => state.newsales.assigned.loading;

export const selectAssignedError = (state) => state.newsales.assigned.error;

export const selectAssignedSuccess = (state) => state.newsales.assigned.success;

export const selectAssignedCurrentPage = (state) => state.newsales.assigned.currentPage;

export const selectAssignedPagesCount = (state) => state.newsales.assigned.pagesCount;

export const selectAssignedSearchData = (state) => state.newsales.assigned.searchData;

export const selectAssignedDeleteSuccess = (state) => state.newsales.assigned.deleteSuccess;

export const selectAssignedUpdateSuccess = (state) => state.newsales.assigned.updateSuccess;
export const selectFollowUps = (state) => state.newsales.followUps.data;

export const selectFollowUpsLoading = (state) => state.newsales.followUps.loading;

export const selectFollowUpsError = (state) => state.newsales.followUps.error;

export const selectFollowUpsUpdateSuccess = (state) => state.newsales.followUps.updateSuccess;

// Passport Data selectors
export const selectPassportData = (state) => state.newsales.passportData.data;
export const selectPassportDataLoading = (state) => state.newsales.passportData.loading;

export const selectLeadById = (state) => state.newsales.leadById.data;
export const selectLeadByIdLoading = (state) => state.newsales.leadById.loading;

export default newsalesSlice.reducer;
